import { useId, useMemo, type ReactNode } from 'react';

import { useProjectFiles } from '../../state/project-files.js';
import { JsonField, NumberField, PropRow, SelectField, TextField, Toggle } from '../ui/controls.js';
import { ParamField } from './ParamField.js';
import { isNum, isObj } from './param-values.js';
import type { FieldSpec } from './sound-catalog.js';

/** Returns a copy of `obj` with `key` set (or removed when `value` is undefined). */
export function withField<T extends Record<string, unknown>>(obj: T, key: string, value: unknown): T {
  const next: Record<string, unknown> = { ...obj };
  if (value === undefined) delete next[key];
  else next[key] = value;
  return next as T;
}

/** Project files matching `ext`, for path pickers. */
export function useProjectPaths(ext?: RegExp): string[] {
  const entries = useProjectFiles((s) => s.entries);
  return useMemo(
    () => entries.filter((e) => e.kind === 'file' && (!ext || ext.test(e.path))).map((e) => e.path),
    [entries, ext],
  );
}

export function PathField({
  value,
  onChange,
  ext,
  placeholder,
}: {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  ext?: RegExp;
  placeholder?: string;
}) {
  const id = useId();
  const paths = useProjectPaths(ext);
  return (
    <>
      <TextField value={value} mono list={id} placeholder={placeholder ?? 'path/in/project'} onChange={(t) => onChange(t.trim() || undefined)} />
      <datalist id={id}>
        {paths.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>
    </>
  );
}

function SuggestField({
  value,
  onChange,
  suggestions,
  placeholder,
  mono,
}: {
  value: string | undefined;
  onChange: (v: string | undefined) => void;
  suggestions?: readonly string[];
  placeholder?: string;
  mono?: boolean;
}) {
  const id = useId();
  return (
    <>
      <TextField value={value} mono={mono} list={suggestions ? id : undefined} placeholder={placeholder} onChange={(t) => onChange(t === '' ? undefined : t)} />
      {suggestions && (
        <datalist id={id}>
          {suggestions.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      )}
    </>
  );
}

const label = (f: FieldSpec) => f.label ?? f.key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

/** One schema field's control. */
export function SchemaControl({
  field,
  value,
  onChange,
  vars,
  duration,
}: {
  field: FieldSpec;
  value: unknown;
  onChange: (value: unknown) => void;
  vars?: Record<string, number>;
  duration?: number;
}) {
  switch (field.kind) {
    case 'param':
      return (
        <ParamField
          value={value}
          onChange={onChange}
          step={field.step}
          min={field.min}
          max={field.max}
          pitch={field.pitch}
          placeholder={field.placeholder}
          vars={vars}
          duration={duration}
        />
      );
    case 'number':
      if (value !== undefined && !isNum(value)) return <JsonField value={value} onChange={onChange} rows={1} />;
      return (
        <NumberField
          value={value as number | undefined}
          step={field.step}
          min={field.min}
          max={field.max}
          integer={field.integer}
          onChange={onChange}
          title={field.placeholder !== undefined ? `Default ${field.placeholder}` : undefined}
        />
      );
    case 'select': {
      const numeric = field.options.every((o) => o !== '' && Number.isFinite(Number(o)));
      return (
        <SelectField
          value={value === undefined ? undefined : String(value)}
          placeholder={field.placeholder ? `${field.placeholder} (default)` : '—'}
          options={field.options}
          onChange={(v) => onChange(v === '' ? undefined : numeric ? Number(v) : v)}
        />
      );
    }
    case 'bool':
      return <Toggle checked={value === true} onChange={(c) => onChange(c ? true : undefined)} />;
    case 'text':
      return <SuggestField value={typeof value === 'string' ? value : undefined} onChange={onChange} suggestions={field.suggestions} placeholder={field.placeholder} mono={field.mono} />;
    case 'path':
      return <PathField value={typeof value === 'string' ? value : undefined} onChange={onChange} ext={field.ext} placeholder={field.placeholder} />;
    case 'json':
    case 'custom':
      return <JsonField value={value} onChange={onChange} rows={2} />;
  }
}

/**
 * Renders `fields` of `obj` as property rows. `custom` fields call
 * `renderCustom` (falling back to JSON). Keys of `obj` not in the schema and
 * not in `skip` appear in an "Other fields" JSON box so nothing is hidden.
 */
export function SchemaFields({
  obj,
  fields,
  onChange,
  vars,
  duration,
  renderCustom,
  skip = ['type'],
}: {
  obj: Record<string, unknown>;
  fields: readonly FieldSpec[];
  onChange: (obj: Record<string, unknown>) => void;
  vars?: Record<string, number>;
  duration?: number;
  renderCustom?: (field: FieldSpec) => ReactNode | undefined;
  skip?: readonly string[];
}) {
  const known = new Set([...fields.map((f) => f.key), ...skip]);
  const extra: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) if (!known.has(k)) extra[k] = v;
  const hasExtra = Object.keys(extra).length > 0;

  return (
    <>
      {fields
        .filter((f) => !f.when || f.when(obj))
        .map((f) => {
          const custom = f.kind === 'custom' ? renderCustom?.(f) : undefined;
          return (
            <PropRow key={f.key} label={label(f)} hint={f.hint}>
              {custom ?? <SchemaControl field={f} value={obj[f.key]} onChange={(v) => onChange(withField(obj, f.key, v))} vars={vars} duration={duration} />}
            </PropRow>
          );
        })}
      {hasExtra && (
        <PropRow label="Other" hint="Fields this editor has no control for (edit as JSON)">
          <JsonField
            value={extra}
            rows={2}
            onChange={(v) => {
              const next: Record<string, unknown> = {};
              for (const [k, val] of Object.entries(obj)) if (known.has(k)) next[k] = val;
              if (isObj(v)) Object.assign(next, v);
              onChange(next);
            }}
          />
        </PropRow>
      )}
    </>
  );
}
